/**
 * Landing エディタ画面（3 カラム: Brief / Page outline / Live preview）。
 *
 * Phase 5 実装: シンプルな最小構成。
 *  - 左: BriefPanel（キャンペーン詳細編集 + 生成設定）
 *  - 中央: PageOutline（セクションリスト + DnD）
 *  - 右: PreviewCanvas（ライブプレビュー）
 *  - 編集 Sheet: SectionEditorSheet（タブ: Content / Style / AI assist）
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ExternalLink, Globe, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useLanding,
  usePublishLanding,
  useUnpublishLanding,
  useUpdateLanding,
} from '@/hooks/useLandings';
import { paths } from '@/routes/paths';

import { BriefPanel } from './components/BriefPanel';
import { PageOutline } from './components/PageOutline';
import { PreviewCanvas } from './components/PreviewCanvas';
import { SectionEditorSheet } from './components/SectionEditorSheet';

import '@goenhub/landing-sections/styles.css';

export function LandingEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: landing, isLoading } = useLanding(id);
  const publishLanding = usePublishLanding();
  const unpublishLanding = useUnpublishLanding();

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    if (landing && !activeSectionId && (landing.sections?.length ?? 0) > 0) {
      setActiveSectionId(landing.sections[0].id);
    }
  }, [landing, activeSectionId]);

  const handlePublish = async () => {
    if (!landing || !id) return;
    if (landing.status === 'published') {
      if (!confirm('公開を取り消しますか?')) return;
      await unpublishLanding.mutateAsync({ landingId: id });
      toast.success('公開を取り消しました');
    } else {
      try {
        const res = await publishLanding.mutateAsync({ landingId: id, slug: landing.slug });
        toast.success(`公開しました: ${res.publicUrl}`);
      } catch (e) {
        toast.error(`公開失敗: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
  };

  if (isLoading || !landing || !id) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[var(--bg-root)]">
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`${paths.campaignsHub}?hub=landings`)}
            aria-label="一覧へ戻る"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <EditableTitle
            landingId={id}
            value={landing.title}
          />
          <span className="text-xs text-[var(--text-muted)]">
            {landing.status === 'published' ? `· /${landing.slug}` : '· 下書き'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ViewportToggle value={viewport} onChange={setViewport} />
          {landing.status === 'published' && landing.slug && (
            <a
              href={`/l/${landing.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline px-2"
            >
              <ExternalLink className="size-3" /> 公開ページ
            </a>
          )}
          <Button size="sm" onClick={handlePublish}>
            <Globe className="size-3.5 mr-1" />
            {landing.status === 'published' ? '公開停止' : '公開'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* 左: Brief */}
        <div className="w-[340px] border-r border-[var(--border)] overflow-y-auto bg-[var(--bg-elevated)]">
          <BriefPanel landingId={id} landing={landing} />
        </div>

        {/* 中央: Outline */}
        <div className="w-[400px] border-r border-[var(--border)] overflow-y-auto bg-[var(--bg-root)]">
          <PageOutline
            landingId={id}
            landing={landing}
            activeSectionId={activeSectionId}
            onSelect={setActiveSectionId}
            onEdit={setEditingSectionId}
          />
        </div>

        {/* 右: Preview */}
        <div className="flex-1 overflow-y-auto bg-[var(--bg-root)]">
          <PreviewCanvas
            landingId={id}
            landing={landing}
            activeSectionId={activeSectionId}
            viewport={viewport}
            onSelectSection={setActiveSectionId}
          />
        </div>
      </div>

      {/* 編集 Sheet */}
      {editingSectionId && (
        <SectionEditorSheet
          landingId={id}
          landing={landing}
          sectionId={editingSectionId}
          onClose={() => setEditingSectionId(null)}
        />
      )}
    </div>
  );
}

/**
 * ランディング名のインライン編集。
 * 表示モード: ホバーで編集アイコン表示、クリックで編集モードへ。
 * 編集モード: Enter で保存、Escape でキャンセル、blur でも保存。
 */
function EditableTitle({ landingId, value }: { landingId: string; value: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const updateLanding = useUpdateLanding();

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next) {
      setDraft(value);
      setEditing(false);
      toast.error('ランディング名は空にできません');
      return;
    }
    if (next === value) {
      setEditing(false);
      return;
    }
    try {
      await updateLanding.mutateAsync({ id: landingId, data: { title: next } });
      setEditing(false);
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onCompositionStart={() => (composingRef.current = true)}
          onCompositionEnd={() => (composingRef.current = false)}
          onKeyDown={(e) => {
            if (composingRef.current) return;
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={() => {
            if (composingRef.current) return;
            void commit();
          }}
          className="h-7 text-sm font-semibold w-[280px]"
          maxLength={80}
          disabled={updateLanding.isPending}
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void commit()}
          title="保存"
          className="size-7 p-0"
        >
          <Check className="size-3.5 text-[var(--accent)]" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          title="キャンセル"
          className="size-7 p-0"
        >
          <X className="size-3.5 text-[var(--text-muted)]" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="クリックしてランディング名を編集"
      className="group flex items-center gap-1.5 px-1.5 py-0.5 -mx-1.5 -my-0.5 rounded hover:bg-[var(--bg-hover)] transition-colors"
    >
      <h1 className="text-sm font-semibold">{value}</h1>
      <Pencil className="size-3 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: 'desktop' | 'tablet' | 'mobile';
  onChange: (v: 'desktop' | 'tablet' | 'mobile') => void;
}) {
  const items: Array<['desktop' | 'tablet' | 'mobile', string]> = [
    ['desktop', '1280'],
    ['tablet', '768'],
    ['mobile', '375'],
  ];
  return (
    <div className="flex border border-[var(--border)] rounded-md overflow-hidden">
      {items.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`px-2.5 py-1 text-[11px] ${
            value === k
              ? 'bg-[var(--accent)] text-white'
              : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
